import { useMemo, useState } from "react"
import { Link } from "react-router-dom"
import { useTranslation } from "@/i18n"
import { useNotifications } from "@/hooks/useNotifications"
import { useEventsByIds, useProfiles } from "@/hooks/useProfiles"
import { notificationLabel } from "@/nostr/api/notificationsLabels"
import { useAuth } from "@/state/AuthContext"
import type { Notification } from "@/nostr/api/notifications"
import { Tabs } from "@/components/Tabs"
import { Avatar } from "@/components/Avatar"
import { Icon } from "@/components/Icon"
import { EmptyState, Skeleton } from "@/components/Feedback"
import { Timestamp } from "@/components/Timestamp"
import { noteExcerpt } from "@/utils/content"
import { noteIdOf, npubOf } from "@/utils/nip19"

type Filter = "all" | "mentions" | "reactions" | "follows"

/** Notifications: mentions, replies, reactions, reposts and new followers. */
export function NotificationsPage() {
  const { t } = useTranslation()
  const { pubkey } = useAuth()
  const { grouped, loading } = useNotifications()
  const [filter, setFilter] = useState<Filter>("all")

  const filtered = useMemo(() => {
    if (filter === "all") return grouped
    if (filter === "mentions") return grouped.filter((group) => group.type === "mention" || group.type === "reply")
    if (filter === "reactions") return grouped.filter((group) => group.type === "reaction" || group.type === "repost")
    return grouped.filter((group) => group.type === "follow")
  }, [grouped, filter])

  if (!pubkey) {
    return (
      <div className="page">
        <EmptyState
          icon="bell"
          title={t("Log in to see notifications")}
          description={t("Notifications are derived from events that tag your public key.")}
          action={
            <Link to="/settings" className="btn btn-primary">
              {t("Settings")}
            </Link>
          }
        />
      </div>
    )
  }

  return (
    <div className="page notifications-page">
      <header className="page-header">
        <div className="page-header-main">
          <h1 className="page-title">{t("Notifications")}</h1>
          <p className="page-subtitle">{grouped.length} events from your relays</p>
        </div>
      </header>

      <Tabs
        value={filter}
        tabs={[
          { value: "all", label: t("All") },
          { value: "mentions", label: t("Mentions") },
          { value: "reactions", label: t("Reactions") },
          { value: "follows", label: t("Follows") },
        ]}
        onChange={(value) => setFilter(value as Filter)}
        ariaLabel={t("Notifications")}
      />

      {loading && filtered.length === 0 && <Skeleton lines={4} />}

      {!loading && filtered.length === 0 && (
        <EmptyState
          icon="bell"
          title={t("Nothing here yet")}
          description={t("When someone replies, reacts or follows, it shows up here.")}
        />
      )}

      <ul className="notification-list">
        {filtered.map((group) => (
          <NotificationRow key={`${group.type}-${group.targetEventId}`} group={group} />
        ))}
      </ul>
    </div>
  )
}

function NotificationRow({
  group,
}: {
  group: { type: Notification["type"]; targetEventId: string; items: Notification[] }
}) {
  const { t } = useTranslation()
  const items = group.items.slice(0, 6)
  const actors = useProfiles(items.map((item) => item.actorPubkey))
  const targetEvents = useEventsByIds([group.targetEventId])
  const target = targetEvents.get(group.targetEventId)
  const newest = items[0]

  return (
    <li className="notification-row">
      <div className="notification-actors">
        {items.slice(0, 3).map((item) => {
          const profile = actors.get(item.actorPubkey)
          return (
            <Link key={item.key} to={`/u/${npubOf(item.actorPubkey)}`} aria-label="Open actor profile">
              <Avatar src={profile?.picture} label={profile?.displayName || item.actorPubkey} size={28} />
            </Link>
          )
        })}
      </div>
      <div className="notification-body">
        <p className="notification-line">
          {items.slice(0, 2).map((item, index) => {
            const profile = actors.get(item.actorPubkey)
            return (
              <span key={item.key}>
                {index > 0 && ", "}
                <Link to={`/u/${npubOf(item.actorPubkey)}`} className="notification-actor">
                  {profile?.displayName || profile?.name || `${item.actorPubkey.slice(0, 8)}…`}
                </Link>
              </span>
            )
          })}
          {items.length > 2 && <span> and {items.length - 2} others</span>}{" "}
          <span className="muted">{notificationLabel(t, group.type)}</span>{" "}
          <Timestamp seconds={newest.createdAt} className="muted small" />
        </p>
        {target ? (
          <Link to={`/n/${noteIdOf(target.id)}`} className="notification-target">
            <Icon name="reply" size={13} />
            <span>{noteExcerpt(target.content, 120) || t("Open note")}</span>
          </Link>
        ) : group.type === "follow" ? (
          <p className="muted small">{t("New follower")}</p>
        ) : null}
      </div>
    </li>
  )
}
